import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Atom,
  CheckCircle2,
  Lightbulb,
  Timer,
  Trophy,
  XCircle,
} from "@/lib/icons";
import GameLayout from "@/games/shared/GameLayout";
import GameMenu from "@/games/shared/GameMenu";
import TurnTimer from "@/games/shared/TurnTimer";
import { LiveAnnouncer, useAnnouncer } from "@/games/shared/LiveAnnouncer";
import { play } from "@/games/shared/sound";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { categoryLabels, type Element } from "@/data/elements";
import ConfigBreakdownPanel from "@/games/electronConfigSprint/ConfigBreakdownPanel";
import { recordMissedElectronConfig } from "@/learn/missedFromPlay";
import {
  ECS_FAST_THRESHOLD_S,
  ECS_POINTS_FAST,
  ECS_POINTS_SLOW,
  ECS_TURN_SECONDS,
  generateQuestion,
  scoreAnswer,
  type EcsDifficulty,
  type EcsOutcome,
  type EcsQuestion,
} from "@/games/electronConfigSprint/engine";

const QUESTIONS_PER_MATCH = 10;
const RESULT_PAUSE_MS = 3200;
const BEST_KEY_PREFIX = "elementpad:electron-config-sprint:best:";

type HistoryEntry = {
  id: string;
  symbol: string;
  name: string;
  config: string;
  outcome: EcsOutcome;
};

const readBest = (difficulty: EcsDifficulty): number => {
  try {
    const raw = window.localStorage.getItem(BEST_KEY_PREFIX + difficulty);
    return raw ? Number(raw) || 0 : 0;
  } catch {
    return 0;
  }
};
const writeBest = (difficulty: EcsDifficulty, score: number) => {
  try {
    window.localStorage.setItem(BEST_KEY_PREFIX + difficulty, String(score));
  } catch {
    /* ignore */
  }
};

function announcement(outcome: EcsOutcome, target: Element, points: number): string {
  switch (outcome) {
    case "fast":
      return `Correct. Fast bonus, plus ${points} points.`;
    case "slow":
      return `Correct. Plus ${points} points.`;
    case "wrong":
      return `Incorrect. The configuration for ${target.name} is ${target.electronConfig}.`;
    case "timeout":
      return `Time's up. The configuration for ${target.name} is ${target.electronConfig}.`;
  }
}

/**
 * Electron Config Sprint - solo timed mode.
 *
 * Player is shown an element and types its ground-state electron
 * configuration. Accepts shorthand ([Ne] 3s² 3p¹) or full expansion,
 * Unicode superscripts or ASCII digits. Difficulty gates which elements
 * appear so beginners are not asked d/f-block configurations.
 */
export default function ElectronConfigSprint() {
  const navigate = useNavigate();
  const [difficulty, setDifficulty] = useState<EcsDifficulty | null>(null);

  return (
    <>
      <LiveAnnouncer />
      {!difficulty ? (
        <DifficultySelect onPick={setDifficulty} />
      ) : (
        <SoloMatch
          difficulty={difficulty}
          onChangeDifficulty={() => setDifficulty(null)}
          onExit={() => navigate("/play")}
        />
      )}
    </>
  );
}

// ---------------- difficulty select ----------------

function DifficultySelect({ onPick }: { onPick: (d: EcsDifficulty) => void }) {
  return (
    <GameLayout
      title="Electron Config Sprint"
      subtitle="Type the electron configuration before the clock runs out."
      headerActions={<GameMenu gameKey="electron-config-sprint" />}
    >
      <div className="max-w-md mx-auto rounded-xl border border-border bg-card p-6">
        <div className="flex items-center gap-2 mb-1">
          <Atom size={16} className="text-primary" aria-hidden="true" />
          <h2 className="text-lg font-semibold text-foreground">Choose difficulty</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-5">
          Higher difficulty means heavier elements and trickier configurations.
        </p>
        <div className="grid grid-cols-1 gap-2 mb-2">
          <DifficultyButton
            label="Easy"
            blurb="Z 1 to 20. s and p blocks only - no transition metals."
            stars={1}
            onClick={() => onPick("easy")}
          />
          <DifficultyButton
            label="Medium"
            blurb="Z 1 to 36. Adds 3d and 4p, including the first row of transition metals."
            stars={2}
            onClick={() => onPick("medium")}
          />
          <DifficultyButton
            label="Hard"
            blurb="All 118 elements. Includes f-block lanthanides and actinides."
            stars={3}
            onClick={() => onPick("hard")}
          />
        </div>
        <p className="text-[11px] text-muted-foreground mt-3">
          {ECS_TURN_SECONDS}s per question · ≤{ECS_FAST_THRESHOLD_S}s ={" "}
          {ECS_POINTS_FAST} pts · slower correct = {ECS_POINTS_SLOW} pts.
        </p>
      </div>
    </GameLayout>
  );
}

function DifficultyButton({
  label,
  blurb,
  stars,
  onClick,
}: {
  label: string;
  blurb: string;
  stars: 1 | 2 | 3;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-left p-4 rounded-md border border-border bg-background hover:border-primary/60 hover:bg-accent/40 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <div className="flex items-center gap-2 mb-1">
        <span className="text-sm font-semibold text-foreground">{label}</span>
        <span aria-label={`Difficulty ${stars} of 3`} className="text-xs text-primary">
          {"★".repeat(stars)}
          <span className="text-muted-foreground">{"★".repeat(3 - stars)}</span>
        </span>
      </div>
      <p className="text-xs text-muted-foreground leading-snug">{blurb}</p>
    </button>
  );
}

// ---------------- solo match ----------------

type Phase =
  | { kind: "playing"; questionIndex: number; question: EcsQuestion; startedAt: number; score: number }
  | {
      kind: "result";
      questionIndex: number;
      question: EcsQuestion;
      input: string | null;
      score: number;
      lastAward: number;
      outcome: EcsOutcome;
    }
  | { kind: "finished"; score: number; best: number };

function makeFirstQuestion(score: number, difficulty: EcsDifficulty): Phase {
  const q = generateQuestion(Math.random, new Set(), difficulty);
  return {
    kind: "playing",
    questionIndex: 0,
    question: q,
    startedAt: performance.now(),
    score,
  };
}

function SoloMatch({
  difficulty,
  onChangeDifficulty,
  onExit,
}: {
  difficulty: EcsDifficulty;
  onChangeDifficulty: () => void;
  onExit: () => void;
}) {
  const [phase, setPhase] = useState<Phase>(() => makeFirstQuestion(0, difficulty));
  const [input, setInput] = useState("");
  const [showHint, setShowHint] = useState(false);
  const askedRef = useRef<Set<string>>(new Set());
  const historyRef = useRef<HistoryEntry[]>([]);
  const advanceTimer = useRef<number | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const announce = useAnnouncer();

  useEffect(() => {
    if (phase.kind === "playing") {
      askedRef.current.add(phase.question.id);
      setInput("");
      setShowHint(false);
      // Defer focus so the input is mounted.
      const id = window.setTimeout(() => inputRef.current?.focus(), 0);
      return () => window.clearTimeout(id);
    }
  }, [phase]);

  useEffect(() => {
    return () => {
      if (advanceTimer.current) window.clearTimeout(advanceTimer.current);
    };
  }, []);

  const submit = useCallback(
    (raw: string | null) => {
      if (phase.kind !== "playing") return;
      const elapsed = performance.now() - phase.startedAt;
      const trimmed = raw === null ? null : raw.trim();
      const value = trimmed === "" ? null : trimmed;
      const { points, outcome } = scoreAnswer(value, phase.question, elapsed);
      const nextScore = phase.score + points;
      const t = phase.question.target;
      historyRef.current.push({
        id: phase.question.id,
        symbol: t.symbol,
        name: t.name,
        config: t.electronConfig,
        outcome,
      });
      if (outcome === "fast" || outcome === "slow") play("correct");
      else if (outcome === "wrong") play("wrong");
      if (outcome === "wrong" || outcome === "timeout") {
        recordMissedElectronConfig(t.symbol, t.name, t.number, t.electronConfig);
      }
      announce(
        announcement(outcome, t, points),
        outcome === "timeout" ? "assertive" : "polite",
      );

      setPhase({
        kind: "result",
        questionIndex: phase.questionIndex,
        question: phase.question,
        input: value,
        score: nextScore,
        lastAward: points,
        outcome,
      });

      advanceTimer.current = window.setTimeout(() => {
        const nextIndex = phase.questionIndex + 1;
        if (nextIndex >= QUESTIONS_PER_MATCH) {
          const best = readBest(difficulty);
          if (nextScore > best) writeBest(difficulty, nextScore);
          play(nextScore > best ? "win" : "click");
          setPhase({
            kind: "finished",
            score: nextScore,
            best: Math.max(best, nextScore),
          });
          return;
        }
        const nextQ = generateQuestion(Math.random, askedRef.current, difficulty);
        askedRef.current.add(nextQ.id);
        setPhase({
          kind: "playing",
          questionIndex: nextIndex,
          question: nextQ,
          startedAt: performance.now(),
          score: nextScore,
        });
      }, RESULT_PAUSE_MS);
    },
    [phase, difficulty, announce],
  );

  const onExpire = useCallback(() => submit(null), [submit]);

  const restart = () => {
    if (advanceTimer.current) window.clearTimeout(advanceTimer.current);
    askedRef.current = new Set();
    historyRef.current = [];
    setPhase(makeFirstQuestion(0, difficulty));
  };

  if (phase.kind === "finished") {
    return (
      <GameLayout
        title="Electron Config Sprint"
        subtitle={`Solo · ${QUESTIONS_PER_MATCH} questions · ${labelFor(difficulty)}`}
        headerActions={
          <GameMenu gameKey="electron-config-sprint" onReset={restart} resetLabel="Restart" />
        }
      >
        <Recap
          history={historyRef.current}
          score={phase.score}
          best={phase.best}
          onExit={onExit}
          onChangeDifficulty={onChangeDifficulty}
          onAgain={restart}
        />
      </GameLayout>
    );
  }

  const active = phase.kind === "playing" ? phase : null;
  const result = phase.kind === "result" ? phase : null;
  const q = active?.question ?? result!.question;
  const idx = active?.questionIndex ?? result!.questionIndex;
  const score = active?.score ?? result!.score;
  const target = q.target;

  return (
    <GameLayout
      title="Electron Config Sprint"
      subtitle={`Solo · ${QUESTIONS_PER_MATCH} questions · ${labelFor(difficulty)}`}
      headerActions={
        <GameMenu gameKey="electron-config-sprint" onReset={restart} resetLabel="Restart" />
      }
    >
      <div className="grid grid-cols-3 gap-3 mb-5">
        <Stat label="Question" value={`${idx + 1} / ${QUESTIONS_PER_MATCH}`} />
        <Stat label="Score" value={String(score)} mono />
        <div className="rounded-lg border border-border bg-card p-3 flex items-center justify-between">
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
            Time
          </span>
          {active ? (
            <TurnTimer
              key={`solo-${q.id}-${idx}`}
              seconds={ECS_TURN_SECONDS}
              onExpire={onExpire}
              size={40}
            />
          ) : (
            <Timer size={18} className="text-muted-foreground" aria-hidden="true" />
          )}
        </div>
      </div>

      <PromptCard target={target} showHint={showHint} onHint={() => setShowHint(true)} disabled={!!result} />

      <form
        className="mt-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (active) submit(input);
        }}
      >
        <label htmlFor="ecs-input" className="sr-only">
          Electron configuration
        </label>
        <div className="flex gap-2">
          <Input
            id="ecs-input"
            ref={inputRef}
            value={result ? (result.input ?? "") : input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="e.g. [Ne] 3s2 3p1"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            disabled={!!result}
            className="font-mono"
          />
          <Button type="submit" disabled={!active || input.trim().length === 0}>
            Submit
          </Button>
        </div>
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
          <div className="rounded-md border border-border bg-card p-3">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
              Shorthand (noble-gas core)
            </p>
            <p className="font-mono text-sm text-foreground">[Ne] 3s² 3p¹</p>
          </div>
          <div className="rounded-md border border-border bg-card p-3">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
              Longhand (full expansion)
            </p>
            <p className="font-mono text-sm text-foreground">1s² 2s² 2p⁶ 3s² 3p¹</p>
          </div>
        </div>
        <p className="mt-2 text-[11px] text-muted-foreground">
          Both forms are accepted. Use ² or 2 for superscripts (e.g. <span className="font-mono">3p2</span> or <span className="font-mono">3p²</span>).
        </p>
      </form>

      {result && (
        <>
          <ResultBanner outcome={result.outcome} award={result.lastAward} target={target} />
          <ConfigBreakdownPanel
            element={target}
            userInput={result.input}
            wasCorrect={result.outcome === "fast" || result.outcome === "slow"}
          />
        </>
      )}
    </GameLayout>
  );
}

function labelFor(d: EcsDifficulty): string {
  return d === "easy" ? "Easy (Z 1-20)" : d === "medium" ? "Medium (Z 1-36)" : "Hard (all 118)";
}

// ---------------- subcomponents ----------------

function Stat({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="rounded-lg border border-border bg-card p-3">
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">{label}</p>
      <p className={`text-base font-semibold text-foreground ${mono ? "font-mono tabular-nums" : ""}`}>
        {value}
      </p>
    </div>
  );
}

function PromptCard({
  target,
  showHint,
  onHint,
  disabled,
}: {
  target: Element;
  showHint: boolean;
  onHint: () => void;
  disabled: boolean;
}) {
  // Hint shows the noble-gas core only (without the trailing orbitals),
  // so it nudges without giving the answer away.
  const coreHint = useMemo(() => {
    const m = target.electronConfig.match(/^\[([A-Za-z]{1,2})\]/);
    return m ? `[${m[1]}] ...` : "Starts at 1s.";
  }, [target]);

  return (
    <div className="rounded-xl border border-border bg-card p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
            Element
          </p>
          <div className="flex items-baseline gap-3">
            <span className="text-3xl font-mono font-bold text-foreground">{target.symbol}</span>
            <span className="text-base text-foreground">{target.name}</span>
          </div>
          <div className="flex flex-wrap items-center gap-2 mt-2">
            <Badge variant="secondary" className="text-[11px]">Z = {target.number}</Badge>
            <Badge variant="secondary" className="text-[11px]">Period {target.period}</Badge>
            {target.group != null && (
              <Badge variant="secondary" className="text-[11px]">Group {target.group}</Badge>
            )}
            <Badge variant="outline" className="text-[11px]">
              {categoryLabels[target.category]}
            </Badge>
            <Badge variant="outline" className="text-[11px] font-mono uppercase">
              {target.block}-block
            </Badge>
          </div>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onHint}
          disabled={showHint || disabled}
          className="text-xs h-8"
        >
          <Lightbulb size={12} className="mr-1" aria-hidden="true" />
          {showHint ? "Hint shown" : "Hint"}
        </Button>
      </div>
      {showHint && (
        <p className="mt-3 text-xs text-muted-foreground">
          Starts with: <span className="font-mono text-foreground">{coreHint}</span>
        </p>
      )}
    </div>
  );
}

function ResultBanner({
  outcome,
  award,
  target,
}: {
  outcome: EcsOutcome;
  award: number;
  target: Element;
}) {
  const correct = outcome === "fast" || outcome === "slow";
  return (
    <div
      className={`mt-4 rounded-lg border p-4 ${
        correct
          ? "border-emerald-500/40 bg-emerald-500/10"
          : "border-destructive/40 bg-destructive/10"
      }`}
      role="status"
    >
      <div className="flex items-start gap-3">
        {correct ? (
          <CheckCircle2 size={18} className="text-emerald-500 mt-0.5" aria-hidden="true" />
        ) : (
          <XCircle size={18} className="text-destructive mt-0.5" aria-hidden="true" />
        )}
        <div className="min-w-0">
          <p className="text-sm font-semibold text-foreground">
            {outcome === "fast" && `Correct - fast bonus +${award}`}
            {outcome === "slow" && `Correct +${award}`}
            {outcome === "wrong" && "Not quite"}
            {outcome === "timeout" && "Time's up"}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            {target.name} ({target.symbol}):{" "}
            <span className="font-mono text-foreground">{target.electronConfig}</span>
          </p>
        </div>
      </div>
    </div>
  );
}

function Recap({
  history,
  score,
  best,
  onExit,
  onChangeDifficulty,
  onAgain,
}: {
  history: HistoryEntry[];
  score: number;
  best: number;
  onExit: () => void;
  onChangeDifficulty: () => void;
  onAgain: () => void;
}) {
  const correctCount = history.filter((h) => h.outcome === "fast" || h.outcome === "slow").length;
  return (
    <div className="grid gap-5">
      <div className="rounded-xl border border-border bg-card p-6 text-center">
        <Trophy size={28} className="mx-auto text-primary mb-2" aria-hidden="true" />
        <p className="text-sm text-muted-foreground">Final score</p>
        <p className="text-4xl font-bold font-mono text-foreground tabular-nums mb-1">{score}</p>
        <p className="text-xs text-muted-foreground">
          {correctCount} of {history.length} correct · personal best:{" "}
          <span className="font-mono">{best}</span>
        </p>
      </div>

      <div className="rounded-xl border border-border bg-card p-4">
        <h3 className="text-sm font-semibold text-foreground mb-3">Round recap</h3>
        <ul className="divide-y divide-border">
          {history.map((h, i) => {
            const correct = h.outcome === "fast" || h.outcome === "slow";
            return (
              <li key={`${h.id}-${i}`} className="py-2 flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 min-w-0">
                  {correct ? (
                    <CheckCircle2 size={14} className="text-emerald-500 shrink-0" aria-hidden="true" />
                  ) : (
                    <XCircle size={14} className="text-destructive shrink-0" aria-hidden="true" />
                  )}
                  <span className="font-mono text-sm text-foreground w-10">{h.symbol}</span>
                  <span className="text-sm text-muted-foreground truncate">{h.name}</span>
                </div>
                <span className="font-mono text-xs text-foreground/80 truncate text-right">
                  {h.config}
                </span>
              </li>
            );
          })}
        </ul>
      </div>

      <div className="flex flex-wrap gap-2">
        <Button onClick={onAgain}>Play again</Button>
        <Button variant="outline" onClick={onChangeDifficulty}>Change difficulty</Button>
        <Button variant="ghost" onClick={onExit}>Back to Play</Button>
      </div>
    </div>
  );
}